import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Fingerprint } from "lucide-react";
import { isWebAuthnSupported, checkPasskeyAvailable, loginWithPasskey } from "@/lib/webauthn";
import ThemeToggle from "@/components/ThemeToggle";
import { useBranding } from "@/context/BrandingContext";

const BG = "https://images.pexels.com/photos/6141905/pexels-photo-6141905.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
    const { login, otpRequest, otpVerify, refresh, mfaVerify, mfaSendEmail, stepUpVerify } = useAuth();
    const branding = useBranding();
    const logoH = Math.max(24, Math.min(64, Number(branding.logo_display_height) || 36));
    const layout = branding.logo_layout || "icon_and_text";
    const showIcon = layout !== "text_only";
    const showText = layout !== "icon_only";
    const navigate = useNavigate();
    // If the axios interceptor bounced us here with ?next=<path>, honor that
    // instead of the default role-based landing. Also surface a "session expired"
    // toast if the reason indicates a silent refresh failure.
    const nextParam = (typeof window !== "undefined") ? new URLSearchParams(window.location.search).get("next") : null;
    const reasonParam = (typeof window !== "undefined") ? new URLSearchParams(window.location.search).get("reason") : null;
    const landing = (u) => nextParam
        ? decodeURIComponent(nextParam)
        : (u.role === "super_admin" ? "/admin" : u.role === "client_portal" ? "/portal" : u.role === "partner" ? "/partner" : (["firm_owner", "managing_partner"].includes(u.role) ? "/app" : "/app/my-week"));
    const [mode, setMode] = useState("password");      // password | otp
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [identifier, setIdentifier] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [passkeyAvailable, setPasskeyAvailable] = useState(false);
    const [webauthnSupported, setWebauthnSupported] = useState(false);
    const [mfaState, setMfaState] = useState(null);  // { methods, method, code }
    const [stepUp, setStepUp] = useState(null);      // { challenge_id, otp_sent_to, reason, otp, trust }

    useEffect(() => {
        setWebauthnSupported(isWebAuthnSupported());
        if (reasonParam === "session_expired") {
            toast.error("Your session has expired — please sign in again.");
        }
    }, [reasonParam]);

    const onEmailBlur = async () => {
        if (!email || !isWebAuthnSupported()) { setPasskeyAvailable(false); return; }
        const ok = await checkPasskeyAvailable(email);
        setPasskeyAvailable(ok);
    };

    const submitPasskey = async () => {
        if (!email) { toast.error("Enter your email above, then tap fingerprint again"); return; }
        if (!isWebAuthnSupported()) {
            toast.error("Your browser doesn't support passkeys. Try Chrome, Safari, or Edge on a device with biometrics.");
            return;
        }
        setLoading(true);
        try {
            // Probe availability so we can show a helpful message instead of a cryptic WebAuthn error
            const ok = await checkPasskeyAvailable(email);
            if (!ok) {
                toast.error("No passkey is enrolled for this email yet. Sign in with your password once, then go to Settings → Security → Passkeys to enrol your fingerprint.", { duration: 8000 });
                return;
            }
            const u = await loginWithPasskey(email);
            await refresh();
            toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
            const dest = landing(u);
            navigate(dest);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Passkey login failed";
            toast.error(msg);
        } finally { setLoading(false); }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            if (u?.mfa_required) {
                const methods = u.methods || [];
                setMfaState({ methods, method: methods[0], code: "" });
                toast(`Two-factor required: ${methods.join(", ")}`);
                if (methods.includes("email") && !methods.includes("totp")) {
                    try { await mfaSendEmail(); }
                    catch (err) { console.warn("Auto-send MFA email failed; user can retry manually", err); }
                }
                return;
            }
            if (u?.step_up) {
                setStepUp({
                    challenge_id: u.challenge_id,
                    otp_sent_to: u.otp_sent_to,
                    reason: u.reason,
                    // v2.15.1 — auto-fill the OTP for demo/mock accounts so the
                    // user isn't forced to copy-paste a 6-digit code from a toast.
                    otp: u.mock_code || "",
                    trust: true,
                });
                if (u.mock_code) {
                    toast.success(`Demo account — OTP auto-filled. Click Confirm to continue.`, { duration: 8000 });
                } else {
                    toast(`Confirm new-device login — code sent to ${u.otp_sent_to}`);
                }
                return;
            }
            toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
            const dest = landing(u);
            navigate(dest);
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    };

    const submitMfa = async (e) => {
        e.preventDefault();
        if (!mfaState?.method || !mfaState?.code) { toast.error("Enter the code"); return; }
        setLoading(true);
        try {
            const u = await mfaVerify(mfaState.method, mfaState.code);
            toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
            const dest = landing(u);
            navigate(dest);
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    };

    const submitStepUp = async (e) => {
        e.preventDefault();
        if (!stepUp?.otp || stepUp.otp.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
        setLoading(true);
        try {
            const u = await stepUpVerify(stepUp.challenge_id, stepUp.otp.trim(), !!stepUp.trust);
            toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
            const dest = landing(u);
            navigate(dest);
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    };

    const triggerEmailSend = async () => {
        try { await mfaSendEmail(); toast.success("New code sent to your email"); }
        catch (e) { toast.error(apiError(e)); }
    };

    const sendOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await otpRequest(identifier, identifier.includes("@") ? "email" : "whatsapp");
            setOtpSent(true);
            // Never leak the raw code in a toast for real users. The backend
            // only echoes `mock_code` for the tiny demo-account allowlist —
            // for everyone else we simply confirm the OTP has been dispatched.
            if (r.mock_code) {
                setCode(r.mock_code);
                toast.success("Demo account — OTP auto-filled. Click Verify & sign in.", { duration: 8000 });
            } else {
                const channelHint = identifier.includes("@") ? "email" : "WhatsApp";
                toast.success(`OTP sent to your ${channelHint}. It expires in 5 minutes.`);
            }
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    };

    const submitOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await otpVerify(identifier, code);
            toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
            const dest = landing(u);
            navigate(dest);
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
            <div className="fixed top-4 right-4 z-50"><ThemeToggle/></div>
            {/* Left visual */}
            <div className="hidden lg:block relative">
                <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover grayscale opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-br from-background/50 via-transparent to-background"></div>
                <div className="relative h-full flex flex-col justify-between p-12">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo">
                        {showIcon && (branding.logo_src ? (
                            <img src={branding.logo_src} alt={branding.app_name}
                                style={{ height: logoH, width: "auto", maxWidth: logoH * 2.5 }}
                                className="object-contain"
                                onError={(e) => { e.target.style.display = "none"; }}/>
                        ) : (
                            <div style={{ height: logoH, width: logoH }}
                                className="border border-primary flex items-center justify-center shrink-0">
                                <span className="font-serif text-primary" style={{ fontSize: logoH * 0.45 }}>
                                    {(branding.app_name || "L")[0]}
                                </span>
                            </div>
                        ))}
                        {showText && <div className="font-serif text-[15px]">{branding.app_name || "Law OS"}</div>}
                    </Link>
                    <div>
                        <div className="overline mb-4">A new standard</div>
                        <p className="font-serif text-3xl leading-tight max-w-md">
                            &ldquo;The discipline of the courtroom, now in the software your chambers depend on.&rdquo;
                        </p>
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="lg:hidden flex items-center gap-2 mb-12" data-testid="login-logo-mobile">
                        {showIcon && (branding.logo_src ? (
                            <img src={branding.logo_src} alt={branding.app_name}
                                style={{ height: Math.min(logoH, 40), width: "auto", maxWidth: 100 }}
                                className="object-contain"/>
                        ) : (
                            <div style={{ height: Math.min(logoH, 40), width: Math.min(logoH, 40) }}
                                className="border border-primary flex items-center justify-center shrink-0">
                                <span className="font-serif text-primary" style={{ fontSize: Math.min(logoH, 40) * 0.45 }}>
                                    {(branding.app_name || "L")[0]}
                                </span>
                            </div>
                        ))}
                        {showText && <span className="font-serif text-lg">{branding.app_name || "Law OS"}</span>}
                    </Link>
                    <div className="overline mb-4">Sign in</div>
                    <h1 className="font-serif text-4xl font-light mb-2">Welcome back.</h1>
                    <p className="text-muted-foreground text-sm mb-10">Authorised access only.</p>

                    <div className="flex gap-1 mb-8 text-xs">
                        <button onClick={() => setMode("password")} data-testid="login-tab-password"
                            className={`px-4 py-2 transition-colors ${mode === "password" ? "bg-muted text-foreground border border-border" : "text-muted-foreground hover:text-foreground border border-transparent"}`}>
                            Password
                        </button>
                        <button onClick={() => setMode("otp")} data-testid="login-tab-otp"
                            className={`px-4 py-2 transition-colors ${mode === "otp" ? "bg-muted text-foreground border border-border" : "text-muted-foreground hover:text-foreground border border-transparent"}`}>
                            OTP (SMS / WhatsApp)
                        </button>
                    </div>

                    {stepUp ? (
                        <form onSubmit={submitStepUp} className="space-y-5" data-testid="stepup-form">
                            <div className="bg-background border border-primary/40 p-4">
                                <div className="overline mb-2 text-primary">Confirm new-device login</div>
                                <p className="text-xs text-muted-foreground">
                                    {stepUp.reason === "missing_fingerprint"
                                        ? "Your browser didn't share a device fingerprint. We've emailed a one-time code to verify it's really you."
                                        : "We've never seen this device on your account. We've emailed a one-time code to verify it's really you."}
                                </p>
                                <p className="text-xs text-muted-foreground mt-2">Code sent to <span className="text-foreground">{stepUp.otp_sent_to}</span> · valid 10 minutes.</p>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">6-digit code</label>
                                <input
                                    value={stepUp.otp} onChange={e => setStepUp({...stepUp, otp: e.target.value})} required autoFocus
                                    data-testid="stepup-otp-input" inputMode="numeric" maxLength="6"
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-4 py-3 text-sm placeholder-muted-foreground tracking-[0.3em] text-center"
                                    placeholder="123 456"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                                <input type="checkbox" checked={!!stepUp.trust}
                                    onChange={e => setStepUp({...stepUp, trust: e.target.checked})}
                                    data-testid="stepup-trust-checkbox"
                                    className="accent-primary"/>
                                Trust this device — don't ask again from this browser
                            </label>
                            <button type="submit" disabled={loading} data-testid="stepup-submit-button"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary py-3 font-medium disabled:opacity-50 transition-colors">
                                {loading ? "Verifying…" : "Verify & sign in"}
                            </button>
                            <button type="button" onClick={() => { setStepUp(null); setPassword(""); }}
                                className="text-xs text-muted-foreground hover:text-foreground block w-full text-center mt-2"
                                data-testid="stepup-cancel-button">
                                ← Use a different account
                            </button>
                        </form>
                    ) : mfaState ? (
                        <form onSubmit={submitMfa} className="space-y-5" data-testid="mfa-form">
                            <div className="bg-background border border-primary/40 p-4">
                                <div className="overline mb-2 text-primary">Two-factor authentication</div>
                                <p className="text-xs text-muted-foreground">Pick a method you&apos;ve enrolled and enter the code.</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {mfaState.methods.map(m => (
                                    <button key={m} type="button" onClick={() => setMfaState({...mfaState, method: m, code: ""})}
                                        data-testid={`mfa-method-${m}`}
                                        className={`px-3 py-1.5 text-xs border ${mfaState.method === m ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
                                        {m === "totp" ? "Authenticator app" : m === "email" ? "Email OTP" : "Backup code"}
                                    </button>
                                ))}
                            </div>
                            {mfaState.method === "email" && (
                                <button type="button" onClick={triggerEmailSend} data-testid="mfa-send-email-btn"
                                    className="text-xs text-primary hover:underline">Send a new code to my email</button>
                            )}
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">
                                    {mfaState.method === "totp" ? "6-digit code from your authenticator" : mfaState.method === "email" ? "6-digit code emailed to you" : "Backup code (XXXX-XXXX)"}
                                </label>
                                <input
                                    value={mfaState.code} onChange={e => setMfaState({...mfaState, code: e.target.value})} required autoFocus
                                    data-testid="mfa-code-input" inputMode={mfaState.method === "backup" ? "text" : "numeric"}
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-4 py-3 text-sm placeholder-muted-foreground tracking-[0.3em] text-center"
                                    placeholder={mfaState.method === "backup" ? "ABCD-1234" : "123 456"}
                                />
                            </div>
                            <button type="submit" disabled={loading} data-testid="mfa-submit-button"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary py-3 font-medium disabled:opacity-50 transition-colors">
                                {loading ? "Verifying…" : "Verify & sign in"}
                            </button>
                            <button type="button" onClick={() => setMfaState(null)} className="text-xs text-muted-foreground hover:text-foreground block w-full text-center mt-2">
                                ← Use a different account
                            </button>
                        </form>
                    ) : mode === "password" ? (
                        <form onSubmit={submitPassword} className="space-y-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Email</label>
                                <input
                                    type="email" required value={email} onChange={e => setEmail(e.target.value)}
                                    onBlur={onEmailBlur}
                                    data-testid="login-email-input"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm placeholder-muted-foreground"
                                    placeholder="advocate@firm.com"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Password</label>
                                <input
                                    type="password" required value={password} onChange={e => setPassword(e.target.value)}
                                    data-testid="login-password-input"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm placeholder-muted-foreground"
                                    placeholder="••••••••"
                                />
                            </div>
                            <button type="submit" disabled={loading} data-testid="login-submit-button"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary py-3 font-medium disabled:opacity-50 transition-colors">
                                {loading ? "Signing in…" : "Sign in"}
                            </button>
                            <div className="text-center">
                                <Link to="/forgot-password" data-testid="login-forgot-password-link"
                                    className="text-xs text-muted-foreground hover:text-primary transition-colors">
                                    Forgot your password?
                                </Link>
                            </div>
                            {webauthnSupported && (
                                <button type="button" onClick={submitPasskey} disabled={loading} data-testid="login-passkey-button"
                                    title={passkeyAvailable ? "Sign in with your registered passkey" : "Tap to sign in with your fingerprint passkey (enrol from Settings → Security after your first password sign-in)"}
                                    className="w-full mt-2 bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground py-3 font-medium disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
                                    <Fingerprint className="w-4 h-4" strokeWidth={1.5}/>
                                    {passkeyAvailable ? "Sign in with passkey" : "Sign in with fingerprint / Face ID"}
                                </button>
                            )}
                            {webauthnSupported && !passkeyAvailable && (
                                <p className="text-[11px] text-muted-foreground text-center -mt-1">
                                    First time? Sign in with your password, then enrol a passkey from <span className="text-foreground">Settings → Security</span>.
                                </p>
                            )}
                        </form>
                    ) : !otpSent ? (
                        <form onSubmit={sendOtp} className="space-y-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Email or mobile</label>
                                <input
                                    required value={identifier} onChange={e => setIdentifier(e.target.value)}
                                    data-testid="login-otp-identifier-input"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm placeholder-muted-foreground"
                                    placeholder="+91 99999 99999 or you@firm.com"
                                />
                            </div>
                            <button type="submit" disabled={loading} data-testid="login-otp-send-button"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary py-3 font-medium disabled:opacity-50 transition-colors">
                                {loading ? "Sending…" : "Send code"}
                            </button>
                            <p className="text-xs text-muted-foreground">A 6-digit code will be sent via WhatsApp / SMS. Codes expire in 5 minutes.</p>
                        </form>
                    ) : (
                        <form onSubmit={submitOtp} className="space-y-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Verification code</label>
                                <input
                                    required value={code} onChange={e => setCode(e.target.value)}
                                    data-testid="login-otp-code-input"
                                    maxLength="6"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm font-mono text-center text-2xl tracking-[0.5em] placeholder-muted-foreground"
                                    placeholder="000000"
                                />
                            </div>
                            <button type="submit" disabled={loading} data-testid="login-otp-verify-button"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary py-3 font-medium disabled:opacity-50 transition-colors">
                                {loading ? "Verifying…" : "Verify & sign in"}
                            </button>
                            <button type="button" onClick={() => { setOtpSent(false); setCode(""); }}
                                className="w-full text-xs text-muted-foreground hover:text-foreground">Use a different identifier</button>
                        </form>
                    )}

                    <div className="mt-10 pt-8 border-t border-border flex justify-between text-sm">
                        <span className="text-muted-foreground">New to Law OS?</span>
                        <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">Start trial →</Link>
                    </div>

                    {/* Demo credentials block — auto-hidden in production builds.
                        Shown in dev / preview / Storybook for testing convenience. */}
                    {process.env.NODE_ENV !== "production" && (
                        <div className="mt-6 text-xs text-muted-foreground space-y-1" data-testid="login-demo-creds">
                            <div className="overline mb-2 !text-muted-foreground">Demo credentials</div>
                            <div>Super Admin — admin@innoaim.com / Admin@12345</div>
                            <div>Firm Owner — owner@demofirm.com / Owner@12345</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}